/**
 * Scrollt naar een content-blok na een Livewire-update (filteren, paginering).
 *
 * De Livewire-overzichten (GardenTipOverview, ArticleOverview, ProductOverview)
 * dispatchen `gotoTop` met het scroll_id van het blok. De blade rendert dat id
 * kaal - zie partials/content-blocks.blade.php - dus 'overview', of 'block_<n>'
 * voor blokken zonder scroll_id. Zoek beide vormen; de oude code zocht naar
 * 'block-<id>' en vond daardoor nooit iets, waarna ze naar de top van de pagina
 * sprong.
 *
 * Het blok wordt bovenaan uitgelijnd. De afstand tot de bovenrand komt uit de
 * CSS: `scroll-margin-top` op `.content-block[id]` (_content-block.scss), die
 * rekening houdt met de sticky subsite-navigatie via --sticky-nav-height.
 */
const FOCUS_MARGIN = 24;

function stickyNavHeight() {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-nav-height')) || 0;
}

export function scrollToBlock(id) {
    if (Array.isArray(id)) id = id[id.length - 1];
    if (!id) return;

    const element = document.getElementById(id) || document.getElementById('block_' + id);
    if (!element) return;

    const scrollMargin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
    let top = element.getBoundingClientRect().top + window.scrollY - scrollMargin;

    // WCAG 2.4.11 Focus Not Obscured: houd het gefocuste element in beeld, niet
    // onder de sticky navigatie en niet onder de viewportrand. Past het blok én
    // de focus niet samen in beeld, dan wint de focus.
    const active = document.activeElement;
    if (active && active !== document.body && active !== element) {
        const rect = active.getBoundingClientRect();
        if (rect.height || rect.width) {
            const lowest = rect.bottom + window.scrollY + FOCUS_MARGIN - window.innerHeight;
            const highest = rect.top + window.scrollY - stickyNavHeight() - FOCUS_MARGIN;
            top = Math.min(Math.max(top, lowest), highest);
        }
    }

    // WCAG 2.3.3: geen animatie voor wie reduced motion vraagt. 'instant' en
    // niet 'auto', want 'auto' volgt een eventuele CSS scroll-behavior: smooth.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
        top: Math.max(top, 0),
        left: 0,
        behavior: reduceMotion ? 'instant' : 'smooth',
    });
}

// Meet de hoogte van de sticky subsite-navigatie en zet die als CSS-variabele,
// zodat scroll-margin-top meegroeit bij zoom of tekst die over twee regels loopt.
function trackStickyNav() {
    const nav = document.querySelector('.subsite-navigation');
    if (!nav || !['sticky', 'fixed'].includes(getComputedStyle(nav).position)) return;

    const update = () => document.documentElement.style.setProperty('--sticky-nav-height', nav.offsetHeight + 'px');
    update();

    if ('ResizeObserver' in window) new ResizeObserver(update).observe(nav);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackStickyNav);
} else {
    trackStickyNav();
}
